import { useState } from "react";
import type { FormEvent } from "react";
import { Send } from "lucide-react";
import {
  CHAT_MESSAGE_TOO_LONG_COPY,
  chatMessageTooLong,
} from "@sill/shared/chat/message-limit";

// Composer input (scenario 676, plan 188 task 802): a single rounded "Ask
// Sill..." field with an accent send button, pinned above the trust line.
export function Composer({
  onSend,
  disabled,
  placeholder = "Ask Sill...",
  bordered = true,
}: {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Top divider above the input. Dropped on the full-page surface. */
  bordered?: boolean;
}) {
  const [value, setValue] = useState("");
  const [tooLong, setTooLong] = useState(false);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    // Over the server cap: keep the draft so nothing the user typed is lost.
    if (chatMessageTooLong(trimmed)) {
      setTooLong(true);
      return;
    }
    setTooLong(false);
    onSend(trimmed);
    setValue("");
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={
        bordered
          ? "flex flex-wrap items-center gap-2 border-t border-border-soft px-3 py-2.5"
          : "flex flex-wrap items-center gap-2 px-3 py-2.5"
      }
    >
      <input
        aria-label="Ask Sill"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (tooLong) setTooLong(false);
        }}
        placeholder={placeholder}
        disabled={disabled}
        className="min-h-11 flex-1 rounded-full border border-border bg-card px-4 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <button
        type="submit"
        aria-label="Send"
        disabled={disabled || value.trim() === ""}
        className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-action text-primary-foreground hover:bg-action-hover disabled:pointer-events-none disabled:opacity-50"
      >
        <Send size={16} aria-hidden />
      </button>
      {tooLong ? (
        <p role="alert" className="basis-full px-1 text-xs text-destructive">
          {CHAT_MESSAGE_TOO_LONG_COPY}
        </p>
      ) : null}
    </form>
  );
}
